import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { Field, Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { Form, RangeFilterLayout } from '../../../components';

import css from './PriceFilterForm.module.css';

// Parse the typed string into an integer (or '' when empty/non-numeric).
// Intentionally does NOT clamp against the other handle or the absolute bounds —
// that would snap the value mid-typing and prevent intermediate states like
// deleting digits from the max field when min is non-zero. Clamping happens on
// blur instead (see clampMinOnBlur / clampMaxOnBlur).
const parseInteger = value => {
  if (value === '' || value == null) {
    return '';
  }
  const parsed = Number.parseInt(value, 10);
  return isNaN(parsed) ? '' : parsed;
};

// Clamp the just-edited bound to the absolute range. If the result crosses the
// other handle, the caller pulls the OTHER bound to match — that way the user's
// most recent edit is preserved instead of being silently replaced.
const clampToBounds = (value, absoluteMin, absoluteMax, emptyFallback) => {
  if (value === '' || value == null) return emptyFallback;
  if (value < absoluteMin) return absoluteMin;
  if (value > absoluteMax) return absoluteMax;
  return value;
};

/**
 * PriceFilterForm component
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - The ID
 * @param {boolean} [props.liveEdit] - Whether the filter is live editable
 * @param {boolean} [props.showAsPopup] - Whether the filter is shown as a popup
 * @param {Function} props.onChange - The function to change the filter
 * @param {Function} props.onSubmit - The function to submit the filter
 * @param {Function} props.onCancel - The function to cancel the filter
 * @param {Function} props.onClear - The function to clear the filter
 * @param {boolean} [props.isOpen] - Whether the filter is open
 * @param {Function} props.contentRef - The function to get the content ref
 * @param {Object} props.style - The style
 * @param {number} props.min - The minimum price for the price range filter
 * @param {number} props.max - The maximum price for the price range filter
 * @param {number} props.step - The step for the price range filter
 * @returns {JSX.Element}
 */
const PriceFilterForm = props => {
  const intl = useIntl();
  const { liveEdit = false, onChange, onSubmit, onCancel, onClear, ...rest } = props;

  if (liveEdit && !onChange) {
    throw new Error('PriceFilterForm: if liveEdit is true you need to provide onChange function');
  }

  if (!liveEdit && !(onCancel && onClear && onSubmit)) {
    throw new Error(
      'PriceFilterForm: if liveEdit is false you need to provide onCancel, onClear, and onSubmit functions'
    );
  }

  // In live-edit mode (side panel) values propagate to the parent only on blur
  // (inputs) or once per animation frame while the slider is being dragged.
  // The blur gate lets the user freely pass through intermediate strings (like
  // deleting digits past the min boundary on the max field); the rAF coalesce
  // on the slider avoids a setState per mousemove while keeping the UI in sync.
  const sliderRafRef = useRef(null);
  const [sliderHandles, setSliderHandles] = useState(null);
  const inputFocusCount = useRef(0);

  const propagateValues = values => {
    if (!liveEdit) return;
    const { minPrice, maxPrice, ...restValues } = values;
    onChange({
      minPrice: minPrice === '' ? rest.min : minPrice,
      maxPrice: maxPrice === '' ? rest.max : maxPrice,
      ...restValues,
    });
  };

  const propagateSliderValuesCoalesced = (form, minPrice, maxPrice) => {
    if (sliderRafRef.current != null) return;
    sliderRafRef.current = requestAnimationFrame(() => {
      sliderRafRef.current = null;
      propagateValues({ ...form.getState().values, minPrice, maxPrice });
    });
  };

  const handleSubmit = values => {
    const { minPrice, maxPrice, ...restValues } = values;
    return onSubmit({
      minPrice: minPrice === '' ? rest.min : minPrice,
      maxPrice: maxPrice === '' ? rest.max : maxPrice,
      ...restValues,
    });
  };

  const formCallbacks = liveEdit
    ? { onSubmit: () => null }
    : { onSubmit: handleSubmit, onCancel, onClear };
  return (
    <FinalForm
      {...rest}
      {...formCallbacks}
      render={formRenderProps => {
        const {
          form,
          handleSubmit,
          id,
          showAsPopup = false,
          onClear,
          onCancel,
          isOpen = false,
          contentRef,
          style,
          intl,
          values,
          min = 0,
          max = 1000,
          step = 1,
          currencyPrefix,
          children,
        } = formRenderProps;
        const { minPrice: minPriceRaw, maxPrice: maxPriceRaw } = values;
        const minPrice = typeof minPriceRaw !== 'string' ? minPriceRaw : min;
        const maxPrice = typeof maxPriceRaw !== 'string' ? maxPriceRaw : max;

        const handleCancel = () => {
          // reset the final form to initialValues
          form.reset();
          onCancel();
        };

        const clear = intl.formatMessage({ id: 'PriceFilterForm.clear' });
        const cancel = intl.formatMessage({ id: 'PriceFilterForm.cancel' });
        const submit = intl.formatMessage({ id: 'PriceFilterForm.submit' });

        const classes = classNames(css.root, {
          [css.popup]: showAsPopup,
          [css.isOpenAsPopup]: showAsPopup && isOpen,
          [css.plain]: !showAsPopup,
          [css.isOpen]: !showAsPopup && isOpen,
        });

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            tabIndex="0"
            contentRef={contentRef}
            style={{ minWidth: '300px', ...style }}
          >
            <div className={classNames({ [css.plainInner]: !showAsPopup })}>
            {showAsPopup ? (
              <span className={css.label}>
                <FormattedMessage id="PriceFilterForm.label" />
              </span>
            ) : null}
            <Field name="minPrice" parse={parseInteger}>
              {({ input: minInput }) => (
                <Field name="maxPrice" parse={parseInteger}>
                  {({ input: maxInput }) => (
                    <RangeFilterLayout
                      min={min}
                      max={max}
                      step={step}
                      handles={
                        inputFocusCount.current > 0 && sliderHandles
                          ? sliderHandles
                          : [minPrice, maxPrice]
                      }
                      onSliderChange={handles => {
                        form.change('minPrice', handles[0]);
                        form.change('maxPrice', handles[1]);
                        setSliderHandles(handles);
                        propagateSliderValuesCoalesced(form, handles[0], handles[1]);
                      }}
                      prefix={currencyPrefix}
                      minInputProps={{
                        ...minInput,
                        id: `${id}.minPrice`,
                        placeholder: min,
                        onFocus: event => {
                          inputFocusCount.current += 1;
                          minInput.onFocus(event);
                        },
                        onKeyDown: event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                        },
                        onBlur: event => {
                          inputFocusCount.current -= 1;
                          const clampedMin = clampToBounds(minInput.value, min, max, min);
                          minInput.onChange(clampedMin);
                          const nextMax = clampedMin > maxPrice ? clampedMin : maxPrice;
                          if (nextMax !== maxPrice) form.change('maxPrice', nextMax);
                          minInput.onBlur(event);
                          setSliderHandles([clampedMin, nextMax]);
                          propagateValues({
                            ...form.getState().values,
                            minPrice: clampedMin,
                            maxPrice: nextMax,
                          });
                        },
                      }}
                      maxInputProps={{
                        ...maxInput,
                        id: `${id}.maxPrice`,
                        placeholder: max,
                        onFocus: event => {
                          inputFocusCount.current += 1;
                          maxInput.onFocus(event);
                        },
                        onKeyDown: event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.currentTarget.blur();
                          }
                        },
                        onBlur: event => {
                          inputFocusCount.current -= 1;
                          const clampedMax = clampToBounds(maxInput.value, min, max, max);
                          maxInput.onChange(clampedMax);
                          const nextMin = clampedMax < minPrice ? clampedMax : minPrice;
                          if (nextMin !== minPrice) form.change('minPrice', nextMin);
                          maxInput.onBlur(event);
                          setSliderHandles([nextMin, clampedMax]);
                          propagateValues({
                            ...form.getState().values,
                            minPrice: nextMin,
                            maxPrice: clampedMax,
                          });
                        },
                      }}
                    />
                  )}
                </Field>
              )}
            </Field>

            {liveEdit ? null : (
              <div className={css.buttonsWrapper}>
                <button className={css.clearButton} type="button" onClick={onClear}>
                  {clear}
                </button>
                <button className={css.cancelButton} type="button" onClick={handleCancel}>
                  {cancel}
                </button>
                <button className={css.submitButton} type="submit">
                  {submit}
                </button>
              </div>
            )}
            {children}
            </div>
          </Form>
        );
      }}
    />
  );
};

export default PriceFilterForm;
